import * as React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import { BILLZAPP_PLANS, POS_PORTAL_URL, TRIAL_PERIOD_DAYS } from "../data/billzappPlans";
import PlanPurchaseDialog from "./planPurchaseDialog.jsx";

export const formatRupees = (paise) => `₹${Math.round(paise / 100).toLocaleString("en-IN")}`;

// A plan's price for the chosen billing cycle ("monthly" or "annual"), in paise.
const planPricePaise = (plan, billingCycle) =>
  billingCycle === "annual" ? plan.annual.amountPaise : plan.amountPaise;

// The struck-through "was" price shown for the sale, in paise.
const planWasPaise = (plan, billingCycle) =>
  billingCycle === "annual" ? plan.annual.wasAmountPaise : plan.wasAmountPaise;

// Read out by screen readers, which skip the look of struck-through text.
// (Sizes as strings: in MUI's sx a bare 1 means 100%.)
const visuallyHidden = { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' };

// The struck-through "was" price of the on-sale display (wasAmountPaise in
// data/billzappPlans.js). Screen readers hear "Was ₹5,999, now" before the real price after it.
export function WasPrice({ paise, ...typographyProps }) {
  return (
    <Typography component="del" color="text.secondary" {...typographyProps}>
      <Box component="span" sx={visuallyHidden}>Was </Box>
      {formatRupees(paise)}
      <Box component="span" sx={visuallyHidden}>, now</Box>
    </Typography>
  );
}

// Monthly / Yearly switch.
function BillingCycleToggle({ value, onChange, sx }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, ...sx }}>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(event, cycle) => cycle && onChange(cycle)}
        color="primary"
        sx={{ bgcolor: 'background.paper' }}
      >
        <ToggleButton value="monthly" sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}>
          Monthly
        </ToggleButton>
        <ToggleButton value="annual" sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}>
          Yearly
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}

// BillZapp plan cards with a Monthly / Yearly switch. "Buy now" pays for the plan here
// (PlanPurchaseDialog, which logs in or creates the account first); the free trial is started
// by signing up in the BillZapp web app.
export default function BillZappPlans() {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [buying, setBuying] = useState(null); // the plan being bought
  const isAnnual = billingCycle === "annual";

  return (
    <Box>
      <BillingCycleToggle value={billingCycle} onChange={setBillingCycle} sx={{ mb: 6 }} />

      <Grid container spacing={4} justifyContent="center">
        {BILLZAPP_PLANS.map((plan) => {
          const pricePaise = planPricePaise(plan, billingCycle);
          const wasPaise = planWasPaise(plan, billingCycle);
          const onSale = wasPaise > pricePaise;
          return (
            <Grid item xs={12} md={5} key={plan.id}>
              <Card
                elevation={plan.recommended ? 8 : 4}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  border: '2px solid',
                  borderColor: plan.recommended ? 'primary.main' : 'transparent',
                  position: 'relative',
                  overflow: 'visible',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-5px)',
                    boxShadow: 12
                  }
                }}
              >
                {plan.recommended && (
                  <Chip
                    label="RECOMMENDED"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: -16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontWeight: 'bold'
                    }}
                  />
                )}

                <Typography variant="h4" fontWeight="bold" color="primary" sx={{ mb: onSale ? 1 : 2, mt: 1 }}>
                  {plan.name}
                </Typography>

                {onSale && (
                  <Box>
                    <Chip
                      label={`ON SALE · SAVE ${formatRupees(wasPaise - pricePaise)}`}
                      color="error"
                      size="small"
                      sx={{ fontWeight: 'bold', mb: 1 }}
                    />
                  </Box>
                )}

                <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'baseline', columnGap: 1.5 }}>
                  {onSale && <WasPrice paise={wasPaise} variant="h5" sx={{ fontWeight: 500 }} />}
                  <Typography variant="h3" component="span" fontWeight="bold">
                    {formatRupees(pricePaise)}
                  </Typography>
                  <Typography variant="h6" component="span" color="text.secondary">
                    {isAnnual ? " / year" : " / month"}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {isAnnual ? "Billed once a year." : "Billed every month."}
                </Typography>

                <Box sx={{ textAlign: 'left', mb: 4, flex: 1 }}>
                  {plan.features.map((feature) => (
                    <Typography key={feature} variant="body1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <CheckIcon sx={{ color: 'success.main', mr: 1 }} />
                      {feature}
                    </Typography>
                  ))}
                </Box>

                <Button
                  variant={plan.recommended ? "contained" : "outlined"}
                  size="large"
                  fullWidth
                  onClick={() => setBuying(plan)}
                >
                  Buy now · {formatRupees(pricePaise)}{isAnnual ? " / year" : " / month"}
                </Button>
                <Button size="large" fullWidth href={`${POS_PORTAL_URL}/signup`} sx={{ mt: 1 }}>
                  Or start a {TRIAL_PERIOD_DAYS}-day free trial
                </Button>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box textAlign="center" sx={{ mt: 5 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Already using BillZapp?{" "}
          <Link href={`${POS_PORTAL_URL}/login`} fontWeight="bold">
            Log in to buy or renew your plan
          </Link>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Prices in INR, inclusive of GST. Payments are made securely online through Razorpay.
        </Typography>
      </Box>

      {buying && <PlanPurchaseDialog plan={buying} billingCycle={billingCycle} onClose={() => setBuying(null)} />}
    </Box>
  );
}
